import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { ArticleListProps } from './contracts';
import ArticleListUI from './ArticleListUI';
import { useArticlesQuery, useInfiniteArticlesQuery } from '@data/articles/useArticlesQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader/SkeletonLoader';
import { Article, PaginatedResponse } from '@repo/types';

export function ArticleList({
  teamFromSlug,
  startDate,
  endDate,
  tags: extraTags,
  tagsMatchMode,
  excludeTags,
  excludeIds,
  filterByTags,
  extraData,
  sort,
  order,
  limit = 1,
  lazyLoad = false,
  ...props
}: ArticleListProps) {
  const [searchParams] = useSearchParams();
  const offset =
    searchParams.get('offset') && typeof parseInt(searchParams.get('offset') as string) === 'number'
      ? parseInt(searchParams.get('offset') as string)
      : 1;

  let tags = '';

  if (extraTags) {
    tags = extraTags;
  } else if (filterByTags && searchParams.get('tags')) {
    tags = searchParams.get('tags') as string;
  } else if (teamFromSlug && extraData?.team?.slug) {
    tags = extraData?.team?.slug;
  }

  const queryArgs = {
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
    tags,
    tagsMatchMode: tagsMatchMode ?? undefined,
    limit: limit ?? undefined,
    sort: sort ?? undefined,
    order: order ?? undefined,
    excludeIds: excludeIds ?? undefined,
    excludeTags: excludeTags ?? undefined,
  };

  // Use regular query when lazyLoad is disabled
  const regularQuery = useArticlesQuery({ ...queryArgs, page: offset }, { enabled: !lazyLoad });

  // Use infinite query when lazyLoad is enabled
  const infiniteQuery = useInfiniteArticlesQuery(queryArgs, { enabled: lazyLoad });

  const isLoading = lazyLoad ? infiniteQuery.isLoading : regularQuery.isLoading;
  const error = lazyLoad ? infiniteQuery.error : regularQuery.error;

  // Flatten all articles from infinite query pages
  const allArticles = useMemo(() => {
    if (!lazyLoad || !infiniteQuery.data) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.items);
  }, [lazyLoad, infiniteQuery.data]);

  // Create a synthetic PaginatedResponse for the UI component
  const paginatedArticles: PaginatedResponse<Article> | undefined = useMemo(() => {
    if (lazyLoad && infiniteQuery.data?.pages && infiniteQuery.data.pages.length > 0) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      if (!lastPage) return regularQuery.data;
      return {
        items: allArticles,
        pagination: lastPage.pagination,
      };
    }
    return regularQuery.data;
  }, [lazyLoad, infiniteQuery.data, allArticles, regularQuery.data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={limit * 5 - 1} />
      </div>
    );
  }

  if (!paginatedArticles || error) {
    return null;
  }

  return (
    <ArticleListUI
      paginatedArticles={paginatedArticles}
      allArticles={allArticles}
      lazyLoad={lazyLoad}
      hasNextPage={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      fetchNextPage={infiniteQuery.fetchNextPage}
      {...props}
    />
  );
}
